import { Editor, Node, NodeEntry, Path, Point, Range, Text, Element, Transforms } from 'slate';
import { HEADING_PLUGIN_ID, HeadingLevel } from '..';
import { PARAGRAPH_PLUGIN_ID } from '../../paragraph';
import { HeadingKeywords } from 'generated/graphql';

/**
 * Under normal circumstances, heading-like nodes are upgraded to actual heading nodes.
 * This covers the case where you have a non-heading node that looks like a heading.
 *
 * Ex: "LIVER: Liver is normal" may be a text node, but should be treated as a heading
 */
export const getMaybeHeadingFormatted = (n: Node): string => {
  const pattern = /[A-Za-z\s]+:/;
  const string = Node.string(n);
  if (pattern.test(string)) {
    return string.split(':')[0].trim().replace(/\s+/g, ' ').toLowerCase();
  }
  return '';
};

/**
 * This returns the next node (from a location or from the top of the document)
 * that is either a heading node or a heading-like node.
 */
export const getNextHeadingOrHeadingLikeNode = ({
  editor,
  location,
  level = HeadingLevel.H1,
  filterFn,
}: {
  editor: Editor;
  location?: Range | Path | Point;
  level?: number;
  filterFn?: (n: Node) => boolean;
}): NodeEntry | null => {
  /**
   * Search for a heading-like node.
   * This could be a paragraph node with text that looks like a heading
   * (e.g. "LIVER: Liver is normal")
   * If a heading-like node (paragraph) is found, return the corresponding
   * heading-like text node within it
   */
  if (editor == null || editor.children.length === 0) {
    return null;
  }

  const at = location ?? Editor.start(editor, []);

  const nextHeadingLikeNode = Editor.next(editor, {
    at,
    match: (n: Node) =>
      n.type === PARAGRAPH_PLUGIN_ID &&
      n.shouldForceInline !== true &&
      getMaybeHeadingFormatted(n) !== '' &&
      (filterFn != null ? filterFn(n) : true),
  });
  if (nextHeadingLikeNode != null) {
    const headingText = Editor.nodes(editor, {
      at: nextHeadingLikeNode[1],
      match: (n: Node) =>
        Text.isText(n) &&
        getMaybeHeadingFormatted(n) !== '' &&
        (filterFn != null ? filterFn(n) : true),
    }).next();
    if (headingText.done === false) {
      return headingText.value;
    }
  }

  /**
   * If there is no heading-like node that matches, but there is a
   * proper heading node that matches instead, return it
   */
  const nextHeading = Editor.next(editor, {
    at,
    match: (n: Node) =>
      n.type === HEADING_PLUGIN_ID &&
      typeof n.level === 'number' &&
      level <= n.level &&
      (filterFn != null ? filterFn(n) : true),
  });
  if (nextHeading != null) {
    return nextHeading;
  }

  /**
   * If there are no non-empty heading-like nodes, return the next reasonable
   * node (either the next block paragraph node or last node for the given location)
   */
  return (
    Editor.next(editor, {
      at,
      match: (n: Node) => n.type === PARAGRAPH_PLUGIN_ID,
    }) ?? null
  );
};

export const getComparisonHeading = (
  editor: Editor,
  comparisonHeadingKeywords: HeadingKeywords['comparison']
): NodeEntry | null => {
  const maybeComparisonHeading = getNextHeadingOrHeadingLikeNode({
    editor,
    filterFn: (n: Node) => comparisonHeadingKeywords.includes(getMaybeHeadingFormatted(n)),
  });
  if (maybeComparisonHeading == null) {
    return null;
  }
  const isComparisonHeading = comparisonHeadingKeywords.includes(
    getMaybeHeadingFormatted(maybeComparisonHeading[0])
  );
  if (!isComparisonHeading) {
    return null;
  }
  return maybeComparisonHeading;
};

/**
 * Given a list of sections, this function will return the range of the target section.
 * Ex: If sections = ['COMPARISON'], this function will return the range of the
 * 'COMPARISON' section.
 * Ex: If sections = ['FINDINGS', 'LIVER'], this function will return the range of the
 * 'LIVER' section.
 *
 * @param {Editor} editor
 * @param {string[]} sections
 */
export const getHeadingSectionEdgesByRegex = (
  editor: Editor,
  sections?: Array<string>
): Nullable<Range> => {
  if (sections == null || sections.length === 0) {
    return null;
  }

  let targetHeaderPoint: Path | Point = Editor.start(editor, []);
  let headingNode: NodeEntry | null = null;
  for (let secIx = 0; secIx < sections.length; secIx++) {
    const section = sections[secIx];
    const level = secIx + 1;
    const location = level > 0 && headingNode != null ? headingNode[1] : Editor.start(editor, []);
    headingNode = getNextHeadingOrHeadingLikeNode({
      editor,
      location,
      filterFn: (n: Node) => getMaybeHeadingFormatted(n) === section.toLowerCase(),
      level,
    });
    if (headingNode == null) {
      return null;
    }
    targetHeaderPoint = headingNode[1];
  }
  if (headingNode == null) return null;

  const lastHeadingNode = getNextHeadingOrHeadingLikeNode({
    editor,
    location: targetHeaderPoint,
    filterFn: (n: Node) => !!getMaybeHeadingFormatted(n),
  });

  let anchor: Nullable<Point> = null;
  let focus: Nullable<Point> = null;

  const after = Editor.after(editor, targetHeaderPoint);
  if (after != null) {
    anchor = after;
  } else {
    const end = Editor.end(editor, targetHeaderPoint);
    anchor = end;
  }

  if (lastHeadingNode == null) {
    focus = Editor.end(editor, []);
  } else {
    const before = Editor.before(editor, lastHeadingNode[1]);
    if (before != null && Point.isBefore(before, anchor)) {
      focus = Editor.end(editor, lastHeadingNode[1]);
    } else {
      focus = before;
    }
  }

  if (anchor == null || focus == null) {
    return null;
  }

  return { anchor, focus };
};

export const maybeDeleteEmptyHeadingNodesWithinSelection = (editor: Editor, selection: Range) => {
  if (Range.isCollapsed(selection)) {
    return;
  }

  const selectedNodes = Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
      mode: 'highest',
    })
  ).map(([node]) => node);

  if (
    selectedNodes.length > 1 &&
    selectedNodes[0].type === HEADING_PLUGIN_ID &&
    selectedNodes[selectedNodes.length - 1].type === HEADING_PLUGIN_ID
  ) {
    const pathsOfEmptyHeadingNodesInSelection = Array.from(
      Editor.nodes(editor, {
        at: selection,
        match: (node) => Element.isElement(node) && node.type === HEADING_PLUGIN_ID,
        mode: 'lowest',
      })
    )
      .filter(([node]) => Node.string(node).trim().length == 0)
      .map(([, path]) => path);

    pathsOfEmptyHeadingNodesInSelection.forEach((path) => {
      if (Editor.hasPath(editor, path)) {
        Transforms.removeNodes(editor, { at: path });
      }
    });
  }
};

// Add an initial space in the inline paragraph, if absent
// In order to work for both normal and bracketed text such that the text is not inside the bracket,
// this space should live in a lone text node as the first child.
export const ensureSpaceAfterHeading = (editor: Editor, headingPath: Path) => {
  // Fetch the first point after the heading node
  const afterHeadingPoint = Editor.after(editor, headingPath) || Editor.end(editor, []);
  const initialChar = Editor.string(editor, {
    anchor: afterHeadingPoint,
    focus:
      Editor.after(editor, afterHeadingPoint, { distance: 1, unit: 'character' }) ||
      Editor.end(editor, []),
  });
  if (initialChar !== ' ') {
    // Insert the node at depth 1, right at the start of the inline paragraph
    Transforms.insertNodes(editor, [{ text: ' ' }], { at: afterHeadingPoint.path.slice(0, 2) });
  }
};

/**
 * When the first node in the fragment is a heading, in order to avoid
 * deletion of an inline paragraph and glomming of text into a heading, we
 * need to split the deletion between the heading and the subsequent
 * content. Returns true when it handled the deletion, otherwise false so
 * callers can fall back to their normal deletion path.
 **/
export const maybeDeleteAcrossHeadingBoundary = (
  editor: Editor,
  selection: Range,
  { selectAfterLeadingSpace = false }: { selectAfterLeadingSpace?: boolean } = {}
) => {
  const startPoint = Editor.start(editor, selection);
  const endPoint = Editor.end(editor, selection);
  const [startNode, startPath] = Editor.node(editor, startPoint, {
    depth: 1,
    edge: 'start',
  });
  const [endNode] = Editor.node(editor, endPoint, {
    depth: 1,
    edge: 'end',
  });

  if (
    !(
      Element.isElement(startNode) &&
      startNode.type === HEADING_PLUGIN_ID &&
      // We don't need to do anything special if the entire heading is selected
      !Point.equals(startPoint, Editor.start(editor, startPath)) &&
      // The end node must be a later non-heading node to qualify
      Element.isElement(endNode) &&
      endNode.type !== HEADING_PLUGIN_ID
    )
  ) {
    return false;
  }

  Editor.withoutNormalizing(editor, () => {
    const selectionStart = Editor.start(editor, selection);

    // Trim the end of the new heading
    const newHeadingString = Editor.string(editor, {
      anchor: Editor.start(editor, startPath),
      focus: selectionStart,
    });
    const trimmedHeadingString = newHeadingString.trimEnd();
    const trimDistance = newHeadingString.length - trimmedHeadingString.length;
    const trimStart = Editor.before(editor, selectionStart, {
      distance: trimDistance,
      unit: 'character',
    });

    // Delete the selected part of the heading and the trimmable spaces
    const headingRange = {
      anchor: trimStart || selectionStart,
      focus: Editor.end(editor, startPath),
    };

    if (!Range.isCollapsed(headingRange)) {
      Transforms.delete(editor, { at: headingRange });
    }

    // Add a colon at the end of the new heading
    if (!trimmedHeadingString.endsWith(':')) {
      Transforms.insertText(editor, ':', { at: Editor.end(editor, startPath) });
    }

    // Delete the content outside the heading up to the end of the selection
    let afterHeadingPoint = Editor.after(editor, startPath) || Editor.end(editor, []);
    const remainingRange = {
      anchor: afterHeadingPoint,
      focus: endPoint,
    };
    Transforms.delete(editor, { at: remainingRange, reverse: true });

    // Ensure the following paragraph is inline
    Transforms.setNodes(editor, { shouldForceInline: true }, { at: afterHeadingPoint });

    // Refetch the first point after the heading node, since we have modified the content after
    afterHeadingPoint = Editor.after(editor, startPath) || Editor.end(editor, []);
    const afterHeadingPointRef = Editor.pointRef(editor, afterHeadingPoint);

    ensureSpaceAfterHeading(editor, startPath);

    if (selectAfterLeadingSpace) {
      const pointAfterLeadingSpace = Editor.after(editor, afterHeadingPoint, {
        distance: 1,
        unit: 'character',
      });
      const selectionPoint =
        pointAfterLeadingSpace != null &&
        Editor.string(editor, {
          anchor: afterHeadingPoint,
          focus: pointAfterLeadingSpace,
        }) === ' '
          ? pointAfterLeadingSpace
          : afterHeadingPoint;

      Transforms.select(editor, selectionPoint);
    } else {
      Transforms.select(editor, afterHeadingPointRef.unref() || Editor.end(editor, []));
    }
  });

  return true;
};
